/* eslint-disable react-refresh/only-export-components -- intentional: step exports a helper alongside the component */
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { cn } from '@/lib/cn';
import {
  PROVIDERS,
  clampToProvider,
  estimateClipCostUsd,
  getProviderMeta,
  mattingAddOnUsd,
  providerBlurb,
  tierLabel,
  type Duration,
  type Resolution,
} from '@/lib/editorCapabilities';
import type { VideoClipTransparency } from '@/services/avatarApi';

import type { WizardState } from '../types';

interface Props {
  state: WizardState;
  update: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
  merge: (patch: Partial<WizardState>) => void;
}

/**
 * Capability-gated picker. The (duration, resolution) pair is auto-clamped
 * whenever the user switches provider. Veo flags chroma-key as "auto-upgrades
 * to matting" so the user knows the choice is honored differently there.
 */
export function Step5EngineQuality({ state, update, merge }: Props) {
  const { t } = useTranslation('avatars');
  const transparencyOptions: ReadonlyArray<{ id: VideoClipTransparency; label: string; blurb: string }> = [
    { id: 'none',       label: t('transparency.none'),      blurb: t('editor.step5.blurbNone') },
    { id: 'chroma-key', label: t('transparency.chromaKey'), blurb: t('editor.step5.blurbChromaKey') },
    { id: 'matting',    label: t('transparency.matting'),   blurb: t('editor.step5.blurbMatting') },
  ];
  const provider = useMemo(() => getProviderMeta(state.videoProvider), [state.videoProvider]);

  // When provider changes, clamp duration/resolution into its supported set.
  useEffect(() => {
    const safe = clampToProvider(provider, state.durationSec, state.resolution);
    if (safe.durationSec !== state.durationSec || safe.resolution !== state.resolution) {
      merge({ durationSec: safe.durationSec, resolution: safe.resolution });
    }
  }, [provider.id]);

  const baseCostUsd = estimateClipCostUsd(provider, state.durationSec, state.resolution);
  const mattingUsd = mattingAddOnUsd(state.durationSec, state.videoTransparency);
  const totalUsd = (baseCostUsd + mattingUsd) * state.variants;

  const veoAutoUpgrade =
    provider.chromaKeyAutoUpgrade === true && state.videoTransparency === 'chroma-key';

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-fg">{t('editor.step5.title')}</h1>
        <p className="text-sm text-fg-muted">
          {t('editor.step5.intro')}
        </p>
      </header>

      {/* Engine grid */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase text-fg-muted">{t('animate2d.engine')}</label>
        <div className="grid grid-cols-2 gap-2">
          {PROVIDERS.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => update('videoProvider', p.id)}
              className={cn(
                'flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2 text-left text-xs transition-colors',
                state.videoProvider === p.id
                  ? 'border-brand bg-brand/10 text-fg'
                  : 'border-border bg-bg-secondary text-fg',
              )}
            >
              <span className="flex w-full items-center justify-between">
                <span className="font-bold">{p.label}</span>
                <span className="rounded-full bg-bg px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-fg-muted">
                  {tierLabel(t, p.tier)}
                </span>
              </span>
              <span className="text-[10px] text-fg-muted">{providerBlurb(t, p.id)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Duration — provider-gated */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase text-fg-muted">{t('animate2d.duration')}</label>
        <div className="flex gap-2">
          {provider.durations.map(d => (
            <Button
              key={d}
              variant="scelta"
              forma="tessera"
              tono="tenue"
              pressed={state.durationSec === d}
              className="flex-1"
              onClick={() => update('durationSec', d as Duration)}
            >
              {d}s
            </Button>
          ))}
        </div>
      </div>

      {/* Resolution — provider-gated, hidden if only 1 supported */}
      {provider.resolutions.length > 1 && (
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold uppercase text-fg-muted">{t('animate2d.resolution')}</label>
          <div className="flex gap-2">
            {provider.resolutions.map(r => (
              <Button
                key={r}
                variant="scelta"
                forma="tessera"
                tono="tenue"
                pressed={state.resolution === r}
                className="flex-1"
                onClick={() => update('resolution', r as Resolution)}
              >
                {r}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Variants */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase text-fg-muted">{t('animate2d.variants')}</label>
        <div className="flex gap-2">
          {([1, 2, 3] as const).map(n => (
            <Button
              key={n}
              variant="scelta"
              forma="tessera"
              tono="tenue"
              pressed={state.variants === n}
              className="flex-1"
              onClick={() => update('variants', n)}
            >
              {t('animate2d.variantOption', { count: n })}
            </Button>
          ))}
        </div>
      </div>

      {/* Seed — optional integer for reproducible runs. */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="ed-seed" className="text-xs font-semibold uppercase text-fg-muted">
          {t('animate2d.seed')} <span className="font-normal normal-case text-fg-muted/60">{t('common.optional')}</span>
        </label>
        <input
          id="ed-seed"
          type="number"
          inputMode="numeric"
          value={state.videoSeed}
          onChange={(e) => update('videoSeed', e.target.value)}
          placeholder={t('animate2d.seedPlaceholder')}
          className="rounded-xl bg-bg-secondary px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none"
        />
        <p className="text-[11px] text-fg-muted">
          {t('editor.step5.seedNote')}
        </p>
      </div>

      {/* Transparency */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase text-fg-muted">{t('editor.step5.background')}</label>
        <div className="flex flex-col gap-1.5">
          {transparencyOptions.map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => update('videoTransparency', opt.id)}
              className={cn(
                'flex items-start gap-2 rounded-xl border px-3 py-2 text-left text-xs transition-colors',
                state.videoTransparency === opt.id
                  ? 'border-brand bg-brand/10 text-fg'
                  : 'border-border bg-bg-secondary text-fg-muted',
              )}
            >
              <span className="flex flex-col">
                <span className="font-semibold text-fg">{opt.label}</span>
                <span className="text-[11px] text-fg-muted">{opt.blurb}</span>
              </span>
            </button>
          ))}
          {veoAutoUpgrade && (
            <p className="text-[11px] text-amber-400">
              {t('editor.step5.veoAutoUpgrade', { provider: provider.label })}
            </p>
          )}
        </div>
      </div>

      {/* Live cost */}
      <div className="rounded-xl bg-bg-secondary p-3 text-xs text-fg-muted">
        <p>
          {t('editor.step5.estimatedCost')}{' '}
          <span className="font-semibold text-fg">${totalUsd.toFixed(2)}</span>
          {' '}
          ({provider.label} · {state.durationSec}s · {state.resolution}
          {state.variants > 1 && ` · ${state.variants}×`}
          {state.videoTransparency === 'matting' && ` ${t('editor.step5.costMatting')}`})
        </p>
      </div>
    </section>
  );
}

export function step5Valid(state: WizardState): boolean {
  return !!state.videoProvider;
}
